import { X } from 'lucide-react'
import { useId, type ReactNode, type RefObject } from 'react'

import OverlayMotion from '@/components/motion/OverlayMotion'
import IconButton from '@/components/ui/IconButton'
import { useModalDialog } from '@/components/ui/use-modal-dialog'
import { cn } from '@/lib/utils'

/**
 * Desktop widths. Below `lg` every modal is full-bleed.
 *
 * The numbered sizes are the frame widths the dialogs are drawn at, each
 * already on the 4-grid, so the scale class is the Figma number over four:
 * `520` the Account confirms, New alert and Edit profile, `560` Mutual funds'
 * Sector breakdown (`4798:16853`) and News' Quick preview (`4243:99428`), `640`
 * Create profile and the tool picker, `720` Import, `840` Home's Brief
 * (`6042:238`), `1080` Add transaction. `reader` is for long reading: it grows
 * 720 → 840 → 960 with the window (`lg`, `xl`, `2xl`) and its height cap is the
 * window less 40px top and bottom (see `READER_MAX_HEIGHT`), where every other
 * size stops at 85dvh or 720px, whichever is less. They sit alongside
 * `sm`/`md`/`lg` rather than in place of them, since the three originals still
 * cover every dialog already built against them.
 */
const WIDTHS = {
  sm: 'lg:w-100',
  md: 'lg:w-115',
  lg: 'lg:w-120',
  '520': 'lg:w-130',
  '560': 'lg:w-140',
  '640': 'lg:w-160',
  '720': 'lg:w-180',
  '840': 'lg:w-210',
  '1080': 'lg:w-270',
  reader: 'lg:w-180 xl:w-210 2xl:w-240',
} as const

/**
 * The scrim every modal gets unless it asks otherwise. Flip this to `'blur'`
 * and every modal in the app blurs the page behind it.
 */
const DEFAULT_SCRIM: Scrim = 'dim'

const MAX_HEIGHT = 'lg:max-h-[min(85dvh,--spacing(180))]'
const READER_MAX_HEIGHT = 'lg:max-h-[calc(100dvh---spacing(20))]'

/**
 * `layout="article"`: Home's Brief reader (mock `.mxr`, 2026-10-28). The whole window on a
 * phone; from `lg` a 960 box 24px clear of every window edge, its height fixed rather than
 * fitted, since the caller's own scroller fills it.
 */
const ARTICLE = cn(
  'fixed inset-0 m-0 h-dvh max-h-dvh w-full max-w-none p-0',
  'lg:m-auto lg:h-[calc(100dvh---spacing(12))] lg:max-h-none lg:w-240 lg:max-w-[calc(100vw---spacing(12))] lg:border lg:shadow-(--shadow-modal)',
)

type Scrim = 'dim' | 'blur'

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Drawn in the header, and the dialog's accessible name. */
  title: string
  /**
   * `headline` sets the title as a story's headline, not a name for the
   * dialog: the 32px page-title rung, the dialog's own `h1`, 24px clear of the
   * eyebrow, and a bare "Close" on the ×. Home's Brief (f05). Every other
   * dialog keeps `label`, the 20px `h2`.
   */
  titleStyle?: 'label' | 'headline'
  /**
   * A small line above the title — the date over Home's Brief ("After the
   * close, 2 Bhadra 2083", `6042:238`). Styled as every eyebrow in the app,
   * and not part of the accessible name.
   */
  eyebrow?: ReactNode
  /** One line under the title, and the dialog's accessible description. */
  description?: ReactNode
  /** sm for a confirm, md for a picker, lg for a form, 520-1080 for wider ones. */
  size?: keyof typeof WIDTHS
  /**
   * `dim` is the app's 55% scrim alone. `blur` adds a light backdrop blur on
   * top of it (see `scrim-blur` in globals.css), for a popup the page behind
   * should stop competing with; none under reduced motion. Defaults to
   * `DEFAULT_SCRIM`.
   */
  scrim?: Scrim
  /**
   * Leading art before the title-and-description column — the waving mascot
   * on Account's Log-out confirm (`4236:82974`), the sad one on Portfolio's
   * Delete-profile confirm. Sized and toned by the caller (an `IconTile`, a
   * plain `<img>`); Modal only places it.
   */
  icon?: ReactNode
  /**
   * rest:pa-imp — trailing art between the title column and the close ×: the Import dialog's
   * 56px `mitra-import` (mock `pfb-imp-art`), which the mock draws where the × would be and the
   * owner kept the × beside (P7). Sized by the caller; decorative.
   */
  headArt?: ReactNode
  /** Focused when the modal opens. Falls back to the panel. */
  initialFocusRef?: RefObject<HTMLElement | null>
  /**
   * Where focus goes on close when whatever opened the modal has left the
   * document — a menu row that unmounted with its menu. Without it focus
   * falls to `<body>`.
   */
  returnFocus?: (() => HTMLElement | null) | undefined
  /**
   * The actions: Cancel first as `variant="secondary"`, the action last in
   * its own kind (primary, or destructive for a delete), both `size="lg"`,
   * the dialog-footer height (`4243:89064`). Modal lines them up on the right
   * and styles neither.
   */
  footer?: ReactNode
  /** Sits left of the actions: a count, a hint. */
  footerNote?: ReactNode
  /**
   * rest:pa-imp — where the actions sit from `lg` up: `end` (the default, every dialog) or
   * `start`, the Import dialog's Cancel + Continue on the left (mock `pfb-imp-actions`). Below
   * `lg` they fill the row either way.
   */
  footerAlign?: 'end' | 'start'
  /** `alertdialog` for a confirm that interrupts to ask. */
  role?: 'dialog' | 'alertdialog'
  /**
   * rest:ab — `raised` is the Account dialog (mock `ac-dialog`, COMPARE-ac Shared chrome): the
   * raised fill with a 1px ring and a deeper drop (`--shadow-dialog-raised`) instead of the card
   * fill and hairline, 24 24 20 padding, a square close that lifts to the tooltip grey, 16 between
   * the art and the title, and the actions on the left. `card` (default) is every other dialog.
   */
  skin?: 'card' | 'raised'
  /**
   * `article` hands the whole panel to `children`: Modal draws no header, close, body scroller
   * or footer (`ARTICLE` for the box), so the caller's own header bar, contents rail and
   * reading progress can hang off its own scroller. `title` still names the dialog, through
   * `aria-label`; Escape and the scrim still close it. `size`, `titleStyle`, `eyebrow`,
   * `description`, `icon`, `footer` and `footerNote` are ignored.
   */
  layout?: 'default' | 'article'
  className?: string
  /** Optional: a confirm can be title, description and footer alone. */
  children?: ReactNode
  /**
   * rest:pa-add — sits in the header between the title and the close button: Add transaction's
   * "Shareholder · Sandip · My Portfolio" (mock `pfb-dlg__holder`). Not part of the dialog's name.
   */
  headAside?: ReactNode
  /** rest:pa-add — extra classes on the close button (Add transaction's 36px round close). */
  closeClassName?: string
}

/**
 * The one modal. Every dialog in the app is this shell with a different body,
 * so the header, the close button, the footer and the motion never drift.
 *
 * Figma, from `lg` up: a square panel on the card fill with a hairline edge
 * over the app's one 55% scrim — Home's Brief (`6042:238`) for the panel and
 * its close, New alert (`4243:89064`) for the scrim and the footer. Below it,
 * "Sheet · Add to watchlist · Dark" (2672:10196): the same parts rising from
 * the bottom edge.
 *
 * Opened by state, not by a trigger prop: whatever button sets `open` gets
 * focus back when it closes. Sheet takes a trigger only because it is
 * Popover's stand-in and has to accept Popover's props.
 */
export default function Modal({
  open,
  onOpenChange,
  title,
  titleStyle = 'label',
  eyebrow,
  description,
  size = 'md',
  scrim = DEFAULT_SCRIM,
  icon,
  initialFocusRef,
  returnFocus,
  footer,
  footerNote,
  footerAlign = 'end',
  headArt,
  role = 'dialog',
  layout = 'default',
  skin = 'card',
  className,
  children,
  headAside,
  closeClassName,
}: ModalProps) {
  const raised = skin === 'raised'
  const titleId = useId()
  const descriptionId = useId()
  const { dialogRef, backdropProps } = useModalDialog({
    open,
    onOpenChange,
    initialFocusRef,
    returnFocus,
  })
  const headline = titleStyle === 'headline'
  const Title = headline ? 'h1' : 'h2'

  if (layout === 'article') {
    return (
      <OverlayMotion
        as="dialog"
        kind="modal"
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        {...backdropProps}
        className={cn(
          'hidden flex-col overflow-hidden outline-none open:flex',
          'border-border-subtle bg-card text-card-foreground backdrop:bg-scrim',
          ARTICLE,
          scrim === 'blur' && 'scrim-blur',
          className,
        )}
      >
        {children}
      </OverlayMotion>
    )
  }

  return (
    <OverlayMotion
      as="dialog"
      kind="modal"
      ref={dialogRef}
      // Both are what `showModal()` already conveys; stated so the semantics
      // are the component's, not a host-language mapping's.
      role={role}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      // The fallback landing spot when no field is named: the panel itself,
      // so a screen reader starts at the title rather than the close button.
      tabIndex={-1}
      {...backdropProps}
      className={cn(
        // `hidden open:flex`, never a bare `flex`: any author-origin `display`
        // beats the UA's `dialog:not([open]) { display: none }`, and a bare
        // `flex` leaves every closed modal painted and focusable.
        'm-0 hidden h-fit max-w-none flex-col outline-none open:flex',
        // The card's own fill, not the popover's raised grey: a menu opened
        // inside a dialog still reads as the layer on top.
        'border border-border-subtle bg-card text-card-foreground backdrop:bg-scrim',
        'px-6 pt-5 pb-[calc(var(--space-5)+var(--safe-bottom))]',
        // Below lg: pinned to the bottom edge. `top-auto` releases the UA's
        // `inset: 0` so the box keeps its content height.
        'fixed inset-x-0 top-auto bottom-0 max-h-[85dvh] w-full rounded-t-(--panel-radius) border-b-0',
        // lg and up: `inset: 0` plus `margin: auto` on a fit-content box is
        // how the UA centres a dialog; restated because the sheet undid it.
        'lg:inset-0 lg:m-auto',
        size === 'reader' ? READER_MAX_HEIGHT : MAX_HEIGHT,
        'lg:max-w-[calc(100vw-var(--space-8))] lg:rounded-(--panel-radius) lg:border-b lg:pb-5 lg:shadow-(--shadow-modal)',
        raised &&
          'border-transparent bg-popover pt-6 lg:rounded-none lg:border-transparent lg:shadow-(--shadow-dialog-raised)',
        WIDTHS[size],
        scrim === 'blur' && 'scrim-blur',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="mx-auto -mt-2 mb-3 h-1 w-9 shrink-0 rounded-full bg-border-strong lg:hidden"
      />

      {/* A trailing `headArt` is taller than the 32px close, so the row centres on it. */}
      <div
        className={cn(
          'flex shrink-0',
          raised ? 'gap-4' : 'gap-3',
          headArt ? 'items-center' : 'items-start',
        )}
      >
        {icon}
        {/* `min-h-8` centres a one-line title on the 32px close button; a
            description grows the column and the button stays top-aligned. */}
        <div className="flex min-h-8 min-w-0 flex-1 flex-col justify-center">
          {eyebrow ? (
            <p
              className={cn(
                'text-body-xs text-muted-foreground-subtle',
                headline ? 'mb-6' : 'mb-1',
              )}
            >
              {eyebrow}
            </p>
          ) : null}
          <Title id={titleId} className={headline ? 'text-h-2xl' : 'text-h-section'}>
            {title}
          </Title>
          {description ? (
            <p
              id={descriptionId}
              className={cn('mt-1 text-muted-foreground', raised ? 'text-body-m' : 'text-body-s')}
            >
              {description}
            </p>
          ) : null}
        </div>
        {headArt}
        {headAside}
        <IconButton
          variant={raised ? 'square' : 'muted'}
          className={cn(
            'group/close',
            raised && 'hover:bg-tooltip focus-visible:bg-tooltip',
            closeClassName,
          )}
          aria-label={headline ? 'Close' : `Close ${title}`}
          onClick={() => {
            onOpenChange(false)
          }}
        >
          <X
            className="size-4 transition-[rotate] duration-med ease-out group-hover/close:motion-safe:rotate-90 group-focus-visible/close:motion-safe:rotate-90"
            aria-hidden="true"
          />
        </IconButton>
      </div>

      {/* The only part that scrolls, so the title and the actions stay put.
          The 4px of pull-back gives a field's focus ring room the overflow
          would otherwise clip.

          `flex-auto` (flex: 1 1 auto), not `flex-1` (flex: 1 1 0%): the
          dialog is `h-fit`, so its height comes from its children. WebKit
          sizes a fit-content column flex container from each item's FLEX
          BASIS, and `flex-1`'s basis is 0 — this body would contribute
          nothing and the dialog would collapse to header + footer. Chrome
          sizes from content instead, so this only reproduces in Safari.
          `flex-auto`'s basis is the content size in both engines;
          `min-h-0` still lets it shrink and scroll once max-height binds. */}
      {children ? (
        <div className="-mx-1 mt-4 flex min-h-0 flex-auto flex-col overflow-y-auto overscroll-contain scrollbar-slim px-1 pb-1">
          {children}
        </div>
      ) : null}

      {footer ? (
        <div className="mt-4 flex shrink-0 flex-wrap items-center gap-2">
          {footerNote ? (
            <div className="min-w-0 flex-1 text-body-xs text-muted-foreground">{footerNote}</div>
          ) : null}
          <div
            className={cn(
              'flex max-lg:w-full max-lg:*:flex-1',
              raised ? 'gap-3' : 'gap-2',
              !raised && footerAlign === 'end' ? 'ml-auto' : null,
            )}
          >
            {footer}
          </div>
        </div>
      ) : null}
    </OverlayMotion>
  )
}
